import type { FC, ReactNode } from "react";
import { Platform, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Text } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import { reportUpdateTimeLabel, type ReportUpdateTimeContext } from "../../report-update-time";

export interface WhatYoullReceivePanelProps extends ReportUpdateTimeContext {
  readonly siteName: string;
  /** What the emailed workbook is saved as, e.g. from `roadsenseWorkbookFilename`. */
  readonly exampleFileName: string;
}

/**
 * The side panel describing the report email's attachment. The page lays it
 * out beside the form, or under it on narrow screens.
 */
export const WhatYoullReceivePanel: FC<WhatYoullReceivePanelProps> = ({
  siteName,
  exampleFileName,
  now,
  viewerTimeZone,
}) => (
  <View style={styles.panel}>
    <View style={styles.heading}>
      <Text textStyle="sm/semibold" color="text">
        What you&apos;ll receive
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        Same RoadSense workbook as today
      </Text>
    </View>
    <View role="list" style={styles.list}>
      <Bullet>The same RoadSense Excel workbook you download today. The file does not change.</Bullet>
      <Bullet>
        <Text textStyle="xs/regular" color="text">
          One file for {siteName}, named like this, for example:
        </Text>
        <View style={styles.fileName}>
          <Text textStyle="xxs/regular" color="text" style={styles.mono}>
            {exampleFileName}
          </Text>
        </View>
      </Bullet>
      <Bullet>Each file covers the hire from its start to yesterday.</Bullet>
      <Bullet>{`Sent after the report updates at ${reportUpdateTimeLabel({ now, viewerTimeZone })}.`}</Bullet>
    </View>
  </View>
);

const Bullet: FC<{ readonly children: ReactNode }> = ({ children }) => (
  <View role="listitem" style={styles.item}>
    <Text textStyle="xs/regular" color="text" aria-hidden>
      •
    </Text>
    <View style={styles.itemBody}>
      {typeof children === "string" ?
        <Text textStyle="xs/regular" color="text">
          {children}
        </Text>
      : children}
    </View>
  </View>
);

const styles = StyleSheet.create((theme) => ({
  panel: {
    gap: theme.spacing["3xl"],
  },
  heading: {
    gap: theme.spacing.xxs,
  },
  list: {
    gap: theme.spacing.lg,
  },
  item: {
    flexDirection: "row",
    gap: theme.spacing.md,
  },
  itemBody: {
    flex: 1,
    gap: theme.spacing.md,
  },
  fileName: {
    padding: theme.spacing.md,
    borderRadius: theme.spacing.xs,
    backgroundColor: theme.colors.surfaceCardInner,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  mono: {
    fontFamily: Platform.select({ ios: "Menlo", default: "monospace" }),
  },
}));
